import {
  Badge,
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FlexContainer,
  IconLibrary,
  ReemaThemeV2,
  RoundedMenu,
  useDisplayModeContext,
} from '@act/storybook';
import { appNotificationRequests } from 'redux/requests/app-notification.requests';
import { useApiCall } from 'redux/utils/api-hook';
import NotificationCenterContent from '../../notification-center/notification-center-content';
import NotificationSkeleton from '../../notification-center/notification-skeleton';
import { appNotificationAppSelectors } from 'redux/slices/appNotificationSlice';
import { useMobile } from '@act/utilities/mobile-context';

export type NotificationFilter = 'all' | 'unreadOnly';

const NotificationBellMenu = () => {
  const { displayMode, isAnimating } = useDisplayModeContext();
  const isCompact = displayMode === 'compact';
  const { isMobile } = useMobile();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [isHovered, setIsHovered] = useState(false);

  const [notificationsFilter, setNotificationsFilter] =
    useState<NotificationFilter>('all');

  const notificationLoadingStatus =
    appNotificationAppSelectors.useSelectAppNotificationLoadingStatus();
  const notificationGroups =
    appNotificationAppSelectors.useSelectAppNotificationGroups();
  const notifications = appNotificationAppSelectors.useSelectAppNotifications();
  const notificationCount: number = notifications?.length || 0;

  const isLoading = notificationLoadingStatus === 'loading';

  const labelVariants = {
    hidden: { opacity: 0, transition: { duration: 0.2 } },
    visible: { opacity: 1, transition: { duration: 0.2, delay: 0.5 } },
  };

  const [
    _markAppNotificationsAsSeenResponse,
    _isMarkingAllAsSeen,
    markAppNotificationsAsSeen,
  ] = useApiCall(appNotificationRequests.markAppNotificationsAsSeen);

  const [
    _clearNotificationsResponse,
    _isClearingNotifications,
    clearNotifications,
  ] = useApiCall(appNotificationRequests.deleteAppNotifications);

  const filteredNotifications =
    notificationsFilter === 'all'
      ? notifications
      : notifications?.filter(n => !n.seenAt);

  const hasNotifications = Boolean(
    filteredNotifications?.length && notificationGroups?.length
  );

  function handleOpen(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleFilter(event: SelectChangeEvent<NotificationFilter>) {
    setNotificationsFilter(event.target.value as NotificationFilter);
  }

  function handleBatchAction(action: 'markAsSeen' | 'delete') {
    const ids = notifications.map(n => n.id);

    if (action === 'markAsSeen') {
      markAppNotificationsAsSeen({
        body: {
          notificationIds: ids,
          seen: true,
        },
        params: null,
      });
    } else {
      clearNotifications({
        params: { notificationIds: ids },
      });
    }
  }

  return (
    <>
      <Button
        className="preserve-icon-size"
        variant="text"
        sx={{
          textTransform: 'none',
          margin: '0',
          padding: '0',
          boxSizing: 'border-box',
          borderRadius: ReemaThemeV2.LIGHT_MODE.app.borderRadius.lessRounded,
          width: '100%',
          overflow: 'hidden',
          flexShrink: 0,
        }}
        disableRipple
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleOpen}
      >
        <FlexContainer
          direction="row"
          gap={2}
          padding={2}
          alignItems="center"
          width="100%"
          overflow="hidden"
          flexShrink={0}
          hoverBackgroundColor={ReemaThemeV2.LIGHT_MODE.palette.secondary.main}
          hoverColor={ReemaThemeV2.LIGHT_MODE.palette.secondary.contrastText}
          borderRadius="lessRounded"
        >
          <Badge
            color="primaryAccent"
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            badgeContent={notificationCount}
          >
            <IconLibrary
              iconName="BellRinging"
              size={32}
              weight="duotone"
              useContrastColor={isHovered}
            />
          </Badge>
          <motion.div
            variants={labelVariants}
            animate={isCompact && !isAnimating ? 'hidden' : 'visible'}
          >
            {!isCompact && (
              <Typography variant="bodyMedium" component="span" color="inherit">
                Notifications
              </Typography>
            )}
          </motion.div>
        </FlexContainer>
      </Button>
      <RoundedMenu
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        width={isMobile ? '100%' : '400px'}
        isPadded={false}
        handleClose={handleClose}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 2,
            padding: '8px 16px 16px',
          }}
        >
          <Typography variant="titleLarge">Notifications</Typography>
          <Select
            value={notificationsFilter}
            variant="standard"
            onChange={handleFilter}
            disableUnderline
            autoWidth
          >
            <MenuItem value={'all'}>Show all</MenuItem>
            <MenuItem value={'unreadOnly'}>Show unread only</MenuItem>
          </Select>
        </Box>

        <Divider sx={{ marginBottom: 2 }} />

        <Box sx={{ display: 'grid', placeItems: 'center', marginBottom: 2 }}>
          {isLoading && !hasNotifications && <NotificationSkeleton />}
          {!isLoading && !hasNotifications && (
            <FlexContainer direction="column" alignItems="center" gap={1}>
              <IconLibrary iconName="BellSlash" size={64} weight="duotone" />
              <Typography variant="titleMedium">
                No notifications found
              </Typography>
            </FlexContainer>
          )}
          {!isLoading && hasNotifications && (
            <NotificationCenterContent
              notifications={filteredNotifications}
              notificationGroups={notificationGroups}
              onNotificationClick={handleClose}
            />
          )}
        </Box>

        <Divider sx={{ marginBottom: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, marginLeft: 2, marginBottom: 1 }}>
          <Button
            variant="text"
            size="small"
            disabled={!hasNotifications}
            onClick={() => handleBatchAction('markAsSeen')}
          >
            Mark all as read
          </Button>
          <Button
            variant="text"
            size="small"
            disabled={!hasNotifications}
            onClick={() => handleBatchAction('delete')}
          >
            Clear all
          </Button>
        </Box>
      </RoundedMenu>
    </>
  );
};

NotificationBellMenu.displayName = 'NotificationBellMenu';

export default NotificationBellMenu;
